import { createReducer } from '@reduxjs/toolkit'
import { loadFormData } from 'apps/builder/store/actions/form'
import { importFormData } from 'apps/builder/store/actions/importData'
import {
  orderByPosition,
  reIndex,
  reIndexOnDelete,
  affectedEntrySetByGuid,
} from 'apps/builder/store/helpers'
import {
  isTrueStringMatch,
  sheetType,
  NORMAL_SHEET,
} from 'apps/builder/lib/utils'
import { deterministicGuid } from 'apps/builder/lib/hash'
import {
  addEntry,
  updateEntry,
  updateIndex,
  deleteEntry,
  copyEntry,
} from 'apps/builder/store/actions/entries'
import {
  deleteSheet,
  deleteChildSheet,
  copySheet,
  splitSheet,
  addTableSheet,
  addListSheet,
  copyTableOrSimpleLoopSheet,
  deleteLoopSheet,
  updateSheetSection,
  convertToLoopScreen,
  promoteToLoopScreen,
  convertToTableScreen,
  promoteToTableScreen,
  editSheet,
  convertListToScreen,
  convertScreenToList,
} from 'apps/builder/store/actions/sheets'
import {
  addReferenceDataEntry,
  updateReferenceDataEntry,
  deleteReferenceDataEntry,
} from 'apps/builder/store/actions/referenceData'
import {
  addLiveDataEntry,
  updateLiveDataEntry,
  deleteLiveDataEntry,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

const appendKeyField = (state, action) => {
  const { keyField, id: oldSheetId, newSheetId } = action.payload
  const affectedEntries = affectedEntrySetByGuid(state, [oldSheetId])

  return [...state, keyField].map(entry => {
    const { dependent_entry, sheet_id, summary_entry } = entry
    // if a field depended on a field in the old sheet...
    if (isTrueStringMatch(dependent_entry?.sheet_id, oldSheetId)) {
      // but it wasn't old sheet anymore, remove the dependency
      if (!isTrueStringMatch(sheet_id, oldSheetId)) {
        entry = {
          ...entry,
          dependent_entry: null,
        }
      } else {
        // otherwise update the dependency to point to the new key field screen
        entry = {
          ...entry,
          dependent_entry: {
            ...dependent_entry,
            sheet_id: newSheetId,
          },
        }
      }
    }
    if (summary_entry && affectedEntries.has(summary_entry?.guid)) {
      entry = {
        ...entry,
        summary_entry: {},
      }
    }

    return entry
  })
}

const promoteKeyField = (state, action) => {
  const { keyField, id: oldSheetId, oldPosition } = action.payload
  const affectedEntries = affectedEntrySetByGuid(state, [oldSheetId])

  // existing keyfield is removed from the old sheet
  const updatedEntries = reIndexOnDelete(
    [...state.filter(e => e.guid !== keyField.guid), keyField],
    oldPosition,
    oldSheetId
  )

  return updatedEntries.map(entry => {
    const { dependent_entry, sheet_id, summary_entry } = entry
    if (summary_entry && affectedEntries.has(summary_entry?.guid)) {
      entry = {
        ...entry,
        summary_entry: {},
      }
    }

    if (isTrueStringMatch(dependent_entry?.sheet_id, oldSheetId)) {
      if (isTrueStringMatch(sheet_id, oldSheetId)) {
        // if a field depended on a field in the old sheet,
        // and it was on the old sheet, it just needs to be updated
        return {
          ...entry,
          dependent_entry: {
            ...dependent_entry,
            sheet_id: keyField.sheet_id,
          },
        }
      } else {
        // if the entry depended on a field in the old sheet,
        // but it wasn't on the old sheet, remove the dependency
        return {
          ...entry,
          dependent_entry: null,
        }
      }
    }

    // otherwise unaffected
    return entry
  })
}

export const updateEntriesPosition = (
  { sheet_id, guid, position },
  sheetEntriesCount
) => {
  return entry => {
    if (entry.sheet_id !== sheet_id || entry.guid === guid) {
      return entry
    }

    const newPosition = reIndex(sheetEntriesCount + 1, position, entry.position)
    return {
      ...entry,
      position: newPosition,
    }
  }
}

// needs to update and/or delete dependent entries, per builder 2
const updateReferenceData = (state, action) => {
  const { guid, ...updatedProps } = action.payload
  const {
    reference_data_id: existingReferenceDataId,
    reference_data_column: existingColumn,
    live_data_source_id: existingLiveDataSourceId,
    live_data_column: existingLiveDataColumn,
  } = state.find(({ guid: entryGuid }) => guid === entryGuid) || {}
  return state.map(entry => {
    const { dependent_entry } = entry
    const dependsOnUpdatedEntry = isTrueStringMatch(dependent_entry?.guid, guid)
    // skip unaffected entries
    if (entry.guid !== guid && !dependsOnUpdatedEntry) {
      return entry
    }

    if (dependsOnUpdatedEntry) {
      const {
        reference_data_column,
        reference_data_id,
        live_data_source_id,
        live_data_source_column,
      } = updatedProps

      if (
        (reference_data_id &&
          !isTrueStringMatch(reference_data_id, existingReferenceDataId)) ||
        (reference_data_column && existingColumn !== reference_data_column)
      ) {
        return {
          ...entry,
          dependent_entry: null,
        }
      }

      if (
        (live_data_source_id &&
          !isTrueStringMatch(live_data_source_id, existingLiveDataSourceId)) ||
        (live_data_source_column &&
          existingLiveDataColumn !== live_data_source_column)
      ) {
        return {
          ...entry,
          dependent_entry: null,
        }
      }

      return entry
    }

    return {
      ...entry,
      ...updatedProps,
      live_data_source_id: null,
      live_data_column: null,
      data_source_context_id: null,
    }
  })
}

// needs to delete dependent reference links, per builder 2
const deleteReferenceData = (state, action) => {
  const { guid, style } = action.payload
  return state.map(entry => {
    const { dependent_entry } = entry
    const dependsOnUpdatedEntry = isTrueStringMatch(dependent_entry?.guid, guid)
    if (entry.guid !== guid && !dependsOnUpdatedEntry) return entry

    if (dependsOnUpdatedEntry) {
      return {
        ...entry,
        dependent_entry: null,
      }
    }

    return {
      ...entry,
      style: style || entry.style,
      default: null,
      dependent_entry: null,
      reference_data_id: null,
      reference_data_column: null,
      data_source_context_id: null,
      live_data_source_id: null,
      live_data_column: null,
    }
  })
}

// live data version
const updateLiveData = (state, action) => {
  const { guid, ...updatedProps } = action.payload

  const { dependent_entry: updatedDependentEntry } = updatedProps
  const dependentEntry =
    state.find(
      ({ guid: entryGuid }) => entryGuid === updatedDependentEntry?.guid
    ) || {}

  const {
    live_data_source_id: existingLiveDataSourceId,
    live_data_column: existingLiveDataColumn,
  } = state.find(({ guid: entryGuid }) => guid === entryGuid) || {}
  return state.map(entry => {
    const { dependent_entry, original_type_id, style } = entry
    const dependsOnUpdatedEntry = isTrueStringMatch(dependent_entry?.guid, guid)
    // skip unaffected entries
    if (entry.guid !== guid && !dependsOnUpdatedEntry) {
      return entry
    }

    if (dependsOnUpdatedEntry) {
      const { live_data_column, live_data_source_id } = updatedProps

      if (
        (live_data_source_id &&
          !isTrueStringMatch(live_data_source_id, existingLiveDataSourceId)) ||
        (live_data_column && existingLiveDataColumn !== live_data_column)
      ) {
        return {
          ...entry,
          dependent_entry: null,
        }
      }
      return entry
    }

    const sameLiveDataSource = isTrueStringMatch(
      dependentEntry.live_data_source_id,
      existingLiveDataSourceId
    )

    return {
      ...entry,
      default: original_type_id === 5 && style === 3
        ? ""// reset default value for live data connected fields that are dropdown type
        : entry.default,
      data_source_context_id: sameLiveDataSource
        ? dependentEntry.data_source_context_id
        : entry.data_source_context_id,
      ...updatedProps,
      reference_data_id: null,
      reference_data_column: null,
    }
  })
}

// needs to delete dependent live data links, per builder 2
const deleteLiveData = (state, action) => {
  const { guid, data_source_context_id, style } = action.payload
  return state.map(entry => {
    const { dependent_entry } = entry
    const dependsOnUpdatedEntry = isTrueStringMatch(dependent_entry?.guid, guid)

    if (
      entry.guid !== guid &&
      !dependsOnUpdatedEntry &&
      data_source_context_id !== entry.data_source_context_id
    )
      return entry

    if (dependsOnUpdatedEntry) {
      return {
        ...entry,
        dependent_entry: null,
      }
    }

    return {
      ...entry,
      style: style || entry.style,
      default: null,
      dependent_entry: null,
      data_source_context_id: null,
      live_data_source_id: null,
      live_data_column: null,
    }
  })
}

const determinePosition = (
  { sheet, newPosition, oldSheet, oldPosition },
  entry
) => {
  // if fields are within the same sheet, we're just reordering
  if (oldSheet === sheet) {
    return reIndex(oldPosition, newPosition, entry.position)
  }

  // if we're on the old sheet, the entry is gone, so reduce postion of all entries above the moved entry
  if (entry.sheet_id === oldSheet) {
    return entry.position > oldPosition ? entry.position - 1 : entry.position
  }

  // if we're adding an entry to a new sheet, every entry matching and above needs +1
  return entry.position >= newPosition ? entry.position + 1 : entry.position
}

const adjustIndexes = changes => entry => {
  const { guid, newPosition, sheet, oldSheet } = changes
  // if the entry isn't on the sheets involved, no changes needed
  if (![sheet, oldSheet].includes(entry.sheet_id)) return entry
  // the entry that moved
  if (entry.guid === guid) {
    return {
      ...entry,
      sheet_id: sheet,
      position: newPosition,
    }
  } else {
    const position = determinePosition(changes, entry)
    return {
      ...entry,
      position,
    }
  }
}

const updateReferences =
  ({ guid, sheet, oldSheet }) =>
  entry => {
    const { dependent_entry } = entry
    const { sheet_id } = dependent_entry || {}
    if (
      sheet_id &&
      isTrueStringMatch(sheet_id, oldSheet) &&
      isTrueStringMatch(guid, dependent_entry.guid)
    ) {
      return {
        ...entry,
        dependent_entry: { ...dependent_entry, sheet_id: sheet },
      }
    }
    return entry
  }

// composition of the two functions above
export const adjustIndexesAndReferences = changes => entry =>
  updateReferences(changes)(adjustIndexes(changes)(entry))

const filterDeletedEntries = (state, action) => {
  const { id, additionalSheets = [] } = action.payload
  const sheetsToDelete = [id, ...additionalSheets.map(({ id }) => id)]
  const affectedEntries = affectedEntrySetByGuid(state, sheetsToDelete)
  return state.reduce((acc, entry) => {
    const { dependent_entry, summary_entry } = entry
    if (sheetsToDelete.includes(entry.sheet_id)) {
      return acc
    }
    const updatedEntry = { ...entry }
    if (summary_entry?.guid && affectedEntries.has(summary_entry?.guid)) {
      updatedEntry.summary_entry = {}
    }
    if (dependent_entry && sheetsToDelete.includes(dependent_entry?.sheet_id)) {
      updatedEntry.dependent_entry = null
    }
    return [...acc, updatedEntry]
  }, [])
}

// TODO: Can this be combined with the above function?
export const filterDeletedEntry = (entries, deletedEntryGuid) =>
  entries.reduce((acc, entry) => {
    if (entry.guid == deletedEntryGuid) return acc

    const updatedEntry = { ...entry }

    if (
      entry.dependent_entry &&
      entry.dependent_entry.guid == deletedEntryGuid
    ) {
      updatedEntry.dependent_entry = null
    }

    if (entry.summary_entry && entry.summary_entry.guid == deletedEntryGuid) {
      updatedEntry.summary_entry = {}
    }

    acc.push(updatedEntry)
    return acc
  }, [])

const createTableKeyfield = (state, action) => {
  const { id, keyField: baseField } = action.payload
  const keyField = {
    ...baseField,
    entry_type_id: baseField.id,
    position: 0,
    sheet_id: id,
    keyField: true,
    description: `Untitled col 1`,
    label: `Untitled col 1`,
    required: false,
  }
  return [...state, keyField]
}

export const entriesReducerConfig = {
  [addEntry]: (state, action) => {
    // filter out source
    const { source, ...payload } = action.payload
    const sheetEntriesCount = state.filter(
      entry => entry.sheet_id === payload.sheet_id
    ).length
    return [...state, payload]
      .map(updateEntriesPosition(payload, sheetEntriesCount))
      .sort(orderByPosition)
  },
  [updateEntry]: (state, action) => {
    const { sheetPayload, ...payload } = action.payload
    const { guid } = payload

    return state.map(entry => {
      if (guid !== entry.guid) return entry
      return {
        ...entry,
        ...payload,
      }
    })
  },
  [updateIndex]: (state, { payload }) => {
    return state.map(adjustIndexesAndReferences(payload)).sort(orderByPosition)
  },
  [deleteEntry]: (state, action) => {
    const { guid } = action.payload
    const { position, sheet_id } = state.find(entry => entry.guid === guid)
    const newState = filterDeletedEntry(state, guid)
    return reIndexOnDelete(newState, position, sheet_id)
  },
  [copyEntry]: (state, action) => {
    /** Grab the guid for the source field & rename it to guidPayload */
    const { guid: guidPayload, seed } = action.payload

    /** Find that field by its guid */
    const clonedEntry = state.find(({ guid }) => guid === guidPayload)

    const sheetEntriesCount = state.filter(
      entry => entry.sheet_id === clonedEntry.sheet_id
    ).length

    /** Build new field object & replace the guid on the cloned field */
    const newEntry = {
      ...clonedEntry,
      guid: deterministicGuid(clonedEntry.guid, seed),
      position: clonedEntry.position + 1,
    }

    return [...state, newEntry]
      .map(entry => {
        if (
          !isTrueStringMatch(entry.sheet_id, newEntry.sheet_id) ||
          entry.guid === newEntry.guid
        ) {
          return entry
        }

        const newPosition = reIndex(
          sheetEntriesCount + 1,
          newEntry.position,
          entry.position
        )

        return {
          ...entry,
          position: newPosition,
        }
      })
      .sort(orderByPosition)
  },
  [deleteSheet]: filterDeletedEntries,
  [deleteChildSheet]: (state, action) => {
    const { deleteTopLevelSection, parentSheet } = action.payload
    const updatedState = filterDeletedEntries(state, action)
    return updatedState.map(entry => {
      if (deleteTopLevelSection && entry.sheet_id === parentSheet.id) {
        // Convert parent screen to a normal screen when the last/only child screen is deleted
        return {
          ...entry,
          keyField: false,
        }
      }
      return entry
    })
  },
  [updateSheetSection]: (state, action) => {
    const { id, parent_sheet_id, deleteSection } = action.payload
    const affectedEntriesByGuid = affectedEntrySetByGuid(state, [id])
    return state.map(entry => {
      const { dependent_entry, summary_entry } = entry
      const updatedEntry = { ...entry }

      // if the last sheet in a loop was moved to a new top-level section, the old keyfield
      // will be the only entry on its parent sheet and needs to become a regular field
      if (deleteSection && isTrueStringMatch(entry.sheet_id, parent_sheet_id)) {
        updatedEntry.keyField = false
      }
      // remove reference data connections unless they are to fields within the same sheet
      // TBD: retain references if they are still valid (e.g. sheet w/ dependent_entry moved UP)
      if (
        dependent_entry &&
        !isTrueStringMatch(dependent_entry?.sheet_id, entry.sheet_id)
      ) {
        updatedEntry.dependent_entry = null
      }
      //break mirror references to affected sheets
      // TBD: retain references if they are still valid (e.g. sheet w/ summary entry moved UP)
      if (summary_entry && affectedEntriesByGuid.has(summary_entry?.guid)) {
        updatedEntry.summary_entry = {}
      }
      return updatedEntry
    })
  },
  [deleteLoopSheet]: (state, action) => {
    const { id: oldKeyfieldSheetId, directDescendants } = action.payload
    const firstChildSheetType = sheetType(directDescendants[0])

    if (firstChildSheetType === NORMAL_SHEET) {
      // Move key field into the first child screen
      const newKeyfieldSheetId = directDescendants[0].id
      return state
        .map(entry => {
          const { sheet_id, position, dependent_entry } = entry

          if (isTrueStringMatch(sheet_id, oldKeyfieldSheetId)) {
            return {
              ...entry,
              sheet_id: newKeyfieldSheetId,
              position: 0,
              keyField: false,
            }
          }

          // Increase position of all entries in the first child screen by one to accommodate the new key field
          const entryPosition =
            sheet_id == newKeyfieldSheetId ? position + 1 : position
          // Update any entries with reference data that depended on the key field sheet back to the new sheet id
          const dependentEntry =
            dependent_entry && dependent_entry?.sheet_id == oldKeyfieldSheetId
              ? { ...dependent_entry, sheet_id: newKeyfieldSheetId }
              : dependent_entry

          return {
            ...entry,
            position: entryPosition,
            dependent_entry: dependentEntry,
          }
        })
        .sort(orderByPosition)
    } else {
      // Delete key field when the first child screen is not a normal screen
      return state.filter(entry => entry.sheet_id !== oldKeyfieldSheetId)
    }
  },
  [copySheet]: (state, action) => {
    const {
      id, // old sheet to be copied
      newId, // new sheet id
      seed, // seed for deterministic guids
    } = action.payload
    const refLinks = {} // map of old field guids to new field guids
    const newEntries = state
      .reduce((acc, entry) => {
        if (isTrueStringMatch(entry.sheet_id, id)) {
          const { guid } = entry
          const newGuid = deterministicGuid(entry.guid, seed)

          // track where any same sheet references might have ended up going
          refLinks[guid] = newGuid

          acc.push({
            ...entry,
            sheet_id: newId,
            guid: newGuid,
          })
        }
        return acc
      }, [])
      .map(entry => {
        // update any old references in the new sheet to point to the new guids
        const { dependent_entry } = entry
        const isSameSheetDependent = isTrueStringMatch(
          dependent_entry?.sheet_id,
          id
        )
        if (!dependent_entry || !isSameSheetDependent) return entry
        return {
          ...entry,
          dependent_entry: {
            sheet_id: newId,
            guid: refLinks[dependent_entry.guid],
          },
        }
      })
    return [...state, ...newEntries]
  },
  [splitSheet]: (state, action) => {
    const { id, newId, position } = action.payload
    const movedEntries = state.reduce((acc, entry) => {
      if (entry.sheet_id === id && entry.position > position) {
        acc.push({
          ...entry,
          sheet_id: newId,
          position: acc.length,
        })
      }
      return acc
    }, [])

    return state.map(entry => {
      const { dependent_entry } = entry
      const movedEntry = movedEntries.find(
        updated => updated.guid === entry.guid
      )
      if (
        isTrueStringMatch(dependent_entry?.sheet_id, id) &&
        movedEntries.find(updated => updated.guid === dependent_entry?.guid)
      ) {
        return {
          ...(movedEntry || entry),
          dependent_entry: {
            ...dependent_entry,
            sheet_id: newId,
          },
        }
      }
      return movedEntry || entry
    })
  },
  [addTableSheet]: createTableKeyfield,
  [addListSheet]: createTableKeyfield,
  [copyTableOrSimpleLoopSheet]: (state, action) => {
    const { sheetsToCopy, newSheetGuid, newMultiSheetGuid, seed } =
      action.payload
    const refLinks = {} // map of old field guids to new field guids

    const newEntries = state
      .reduce((acc, entry) => {
        // if the entry is on a sheet that was copied, include it
        if (
          sheetsToCopy.find(sheetId =>
            isTrueStringMatch(entry.sheet_id, sheetId)
          )
        ) {
          const { guid, keyField, dependent_entry, sheet_id } = entry
          const newGuid = deterministicGuid(entry.guid, seed)

          // track where any same sheet references might have ended up going
          refLinks[guid] = newGuid

          const newEntry = {
            ...entry,
            sheet_id: keyField ? newSheetGuid : newMultiSheetGuid,
            guid: newGuid,
          }

          if (dependent_entry) {
            const isSameSheetDependent = isTrueStringMatch(
              dependent_entry?.sheet_id,
              sheet_id
            )
            const isExternalSheetDependent = !sheetsToCopy.find(sheetId =>
              isTrueStringMatch(dependent_entry?.sheet_id, sheetId)
            )

            let sheetId
            if (isSameSheetDependent) {
              // if it's on the same sheet, must be on the same child screen
              sheetId = newMultiSheetGuid
            } else if (isExternalSheetDependent) {
              // if on an external sheet, it doesn't change
              sheetId = dependent_entry.sheet_id
            } else {
              // otherwise, it must be keyfield dependent
              sheetId = newSheetGuid
            }

            newEntry.dependent_entry = {
              sheet_id: sheetId,
              guid: dependent_entry.guid, // need old value for the lookup later
            }
          }
          acc.push(newEntry)
        }
        return acc
      }, [])
      .map(entry => {
        const { dependent_entry } = entry
        if (!dependent_entry) {
          return entry
        }
        // fix dependent_entry so that it points to the new guids
        return {
          ...entry,
          dependent_entry: {
            ...dependent_entry,
            // will either be in lookup or not, in which case it's external/unchanged
            guid: refLinks[dependent_entry.guid] || dependent_entry.guid,
          },
        }
      })

    return [...state, ...newEntries]
  },
  [convertToLoopScreen]: appendKeyField,
  [promoteToLoopScreen]: promoteKeyField,
  [convertToTableScreen]: appendKeyField,
  [promoteToTableScreen]: promoteKeyField,
  [editSheet]: (state, action) => {
    const { id: sheet_id, sheetPayload, updateEntries } = action.payload

    if (!updateEntries) return state

    return state.map(entry => {
      if (entry.sheet_id == sheet_id) {
        return {
          ...entry,
          ...sheetPayload,
        }
      } else {
        return entry
      }
    })
  },
  [saveApp.fulfilled]: (state, action) => {
    const { entities } = action.payload
    const { entries } = entities
    return entries.sort(orderByPosition)
  },
  [loadFormData]: (state, action) => {
    const { entries } = action.payload
    return entries
  },
  [importFormData]: (state, action) => {
    const { entries } = action.payload
    return [...state, ...entries]
  },
  [addReferenceDataEntry]: updateReferenceData,
  [updateReferenceDataEntry]: updateReferenceData,
  [deleteReferenceDataEntry]: deleteReferenceData,
  [addLiveDataEntry]: updateLiveData,
  [updateLiveDataEntry]: updateLiveData,
  [deleteLiveDataEntry]: deleteLiveData,
  [convertListToScreen]: (state, action) => {
    return state.map(entry => {
      if (entry.sheet_id === action.payload.id) {
        return {
          ...entry,
          keyField: false,
        }
      }
      return entry
    })
  },
  [convertScreenToList]: (state, action) => {
    return state.map(entry => {
      const { dependent_entry } = entry
      if (isTrueStringMatch(entry.sheet_id, action.payload.id)) {
        return {
          ...entry,
          keyField: true,
        }
      }
      if (isTrueStringMatch(dependent_entry?.sheet_id, action.payload.id)) {
        return {
          ...entry,
          dependent_entry: null,
        }
      }
      return entry
    })
  },
}

export const entriesReducer = createReducer([], entriesReducerConfig)
